import { Component, Input } from '@angular/core';

interface ParityReport {
  asa: string;
  lastModified: string;
  configMatchScore: string;
  groups: {
    name: string;
    status: string;
    detail: string;
  }[];
  networkObjects: {
    name: string;
    status: string;
    detail: string;
  }[];
}

interface ComparisonResult {
  name: string;
  status: 'Match' | 'Mismatch' | 'Missing';
  detail: string;
}

@Component({
  selector: 'app-download-report',
  standalone: false,
  template: `
    <div class="download-buttons">
      <!-- <button (click)="downloadReport('json')" style="margin-top: 12px; margin-right: 8px;">
        Download JSON Report
      </button> -->
      <button (click)="downloadReport('csv')" style="margin-top: 12px;">
        Download CSV Report
      </button>
    </div>
  `
})
export class DownloadReportComponent {
  @Input() inventory: any;
  @Input() interface: any;
  @Input() dns: any;
  @Input() comparedInventory: any; // Add second ASA data
  @Input() comparedInterface: any;
  @Input() comparedDns: any;

  private compareGroups(): ComparisonResult[] {
    const results: ComparisonResult[] = [];
    if (!this.interface || !this.comparedInterface) return results;

    const sourceGroups = new Set(this.interface.map((i: any) => i.interfaceName));

    sourceGroups.forEach(group => {
      const groupName = String(group);
      const sourceGroup = this.interface.find((i: any) => i.interfaceName === group);
      const targetGroup = this.comparedInterface.find((i: any) => i.interfaceName === group);

      if (!targetGroup) {
        results.push({ name: groupName, status: 'Missing', detail: 'Group not found in target ASA' });
      } else if (JSON.stringify(sourceGroup) !== JSON.stringify(targetGroup)) {
        // Build a detailed difference string
        const diffs: string[] = [];
        Object.keys(sourceGroup).forEach(key => {
          if (sourceGroup[key] !== targetGroup[key]) {
            diffs.push(`${key}: "${sourceGroup[key]}" vs "${targetGroup[key]}"`);
          }
        });
        const detail = diffs.length
          ? `Differences: ${diffs.join('; ')}`
          : 'Configuration differences found in interface settings';
        results.push({ 
          name: groupName, 
          status: 'Mismatch', 
          detail
        });
      } else {
        results.push({ name: groupName, status: 'Match', detail: '-' });
      }
    });

    return results;
  }

  private compareDnsObjects(): ComparisonResult[] {
    const results: ComparisonResult[] = [];
    if (!this.dns || !this.comparedDns) return results;

    const sourceDns = new Set(this.dns.map((d: any) => d.dnsServer));

    sourceDns.forEach(server => {
      const sourceServer = this.dns.find((d: any) => d.dnsServer === server);
      const targetServer = this.comparedDns.find((d: any) => d.dnsServer === server);

      if (!targetServer) {
        results.push({ name: String(server), status: 'Missing', detail: 'DNS server not found in target ASA' });
      } else if (JSON.stringify(sourceServer) !== JSON.stringify(targetServer)) {
        // Build a detailed difference string
        const diffs: string[] = [];
        Object.keys(sourceServer).forEach(key => {
          if (sourceServer[key] !== targetServer[key]) {
            diffs.push(`${key}: "${sourceServer[key]}" vs "${targetServer[key]}"`);
          }
        });
        const detail = diffs.length
          ? `Differences: ${diffs.join('; ')}`
          : 'DNS configuration mismatch';
        results.push({ 
          name: String(server), 
          status: 'Mismatch', 
          detail
        });
      } else {
        results.push({ name: String(server), status: 'Match', detail: 'DNS configuration matches' });
      }
    });

    return results;
  }

  private calculateConfigMatchScore(): string {
    const groupResults = this.compareGroups();
    const dnsResults = this.compareDnsObjects();
    const allResults = [...groupResults, ...dnsResults];
    if (allResults.length === 0) return 'N/A';
    const matchCount = allResults.filter(r => r.status === 'Match').length;
    const percent = Math.round((matchCount / allResults.length) * 100);
    return percent + '%';
  }

  private generateCsvContent(): string {
    let csv = 'Drill-Down View: ASA Parity Status Details\n\n';
    
    // ASA Details
    csv += `Source ASA:,${this.inventory?.hostname || ''}\n`;
    csv += `Target ASA:,${this.comparedInventory?.hostname || ''}\n`;
    csv += `Last Modified:,${this.inventory?.lastModified || ''}\n`;
    csv += `Config Match Score:,${this.calculateConfigMatchScore()}\n`;
    
    // Group Parity
    csv += '\nGroup Parity\n';
    csv += 'Group Name,Status,Detail\n';
    this.compareGroups().forEach(group => {
      csv += `${group.name},${group.status},${group.detail}\n`;
    });
    
    // Network Object Parity
    csv += '\nNetwork Object Parity\n';
    csv += 'Object Name,Status,Detail\n';
    this.compareDnsObjects().forEach(dns => {
      csv += `${dns.name},${dns.status},${dns.detail}\n`;
    });

    return csv;
  }

  downloadReport(format: 'json' | 'csv') {
    if (format === 'json') {
      const report = {
        inventory: this.inventory,
        interface: this.interface,
        dns: this.dns
      };
      const reportStr = JSON.stringify(report, null, 2);
      this.triggerDownload(reportStr, 'application/json', 'json');
    } else {
      const csvContent = this.generateCsvContent();
      this.triggerDownload(csvContent, 'text/csv', 'csv');
    }
  }

  private triggerDownload(content: string, type: string, extension: string) {
    const blob = new Blob([content], { type });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `parity-report-${this.inventory?.hostname || 'inventory'}.${extension}`;
    a.click();
    window.URL.revokeObjectURL(url);
  }
}